/**
 * Agent editor form model: zod schema, server ↔ form mapping, dirty-field
 * PATCH builder and playground overrides.
 */
import type { AgentDetail } from "@/api/generated/AgentDetail"
import type { Overrides } from "@/api/generated/Overrides"
import { type AgentInput, REASONING_EFFORTS } from "@/api/queries/agents"
import { z } from "@/lib/zod"

const nullableNumber = (min: number, max: number, int = false) =>
  z
    .number()
    .min(min)
    .max(max)
    .refine((n) => !int || Number.isInteger(n))
    .nullable()

export const agentSchema = z.object({
  name: z.string().trim().min(1).max(255),
  description: z.string().max(2000),
  icon: z.string().max(64),
  model_id: z
    .number({ error: () => undefined })
    .int()
    .positive(),
  system_prompt: z.string().max(200_000),
  temperature: z.number().min(0).max(2),
  top_p: nullableNumber(0, 1),
  top_k: nullableNumber(0, 10_000, true),
  max_tokens: nullableNumber(1, 1_000_000, true),
  thinking_enabled: z.boolean(),
  reasoning_effort: z.enum(REASONING_EFFORTS),
  prompt_cache_static: z.enum(["5m", "1h"]),
  prompt_cache_messages: z.enum(["5m", "1h"]),
  max_steps: z.number().int().min(1).max(500),
  allow_as_chat_agent: z.boolean(),
  tag_ids: z.array(z.number()),
  skill_ids: z.array(z.number()),
  mcp_servers: z.array(z.object({ mcp_server_id: z.number(), disabled_tools: z.array(z.string()) })),
})

export type AgentFormValues = z.infer<typeof agentSchema>

const asCache = (v: string): "5m" | "1h" => (v === "5m" ? "5m" : "1h")
const asEffort = (v: string): AgentFormValues["reasoning_effort"] =>
  (REASONING_EFFORTS as readonly string[]).includes(v) ? (v as AgentFormValues["reasoning_effort"]) : "medium"

export function agentToForm(a: AgentDetail): AgentFormValues {
  return {
    name: a.name,
    description: a.description,
    icon: a.icon,
    model_id: a.model_id,
    system_prompt: a.system_prompt,
    temperature: a.temperature,
    top_p: a.top_p,
    top_k: a.top_k,
    max_tokens: a.max_tokens,
    thinking_enabled: a.thinking_enabled,
    reasoning_effort: asEffort(a.reasoning_effort),
    prompt_cache_static: asCache(a.prompt_cache_static),
    prompt_cache_messages: a.prompt_cache_messages === "1h" ? "1h" : "5m",
    max_steps: a.max_steps,
    allow_as_chat_agent: a.allow_as_chat_agent,
    tag_ids: [...a.tag_ids],
    skill_ids: [...a.skill_ids],
    mcp_servers: a.mcp_servers.map((m) => ({
      mcp_server_id: m.mcp_server_id,
      disabled_tools: [...m.disabled_tools],
    })),
  }
}

/** Fields that create a new revision when they change (server-side rule). */
export const TRACKED_FIELDS = ["model_id", "system_prompt", "temperature", "top_p", "top_k"] as const

type DirtyMap = Partial<Record<keyof AgentFormValues, unknown>>

function isDirty(v: unknown): boolean {
  if (v === true) return true
  if (Array.isArray(v)) return v.some(isDirty)
  if (v && typeof v === "object") return Object.values(v).some(isDirty)
  return false
}

/** Keys of the form that the user changed. */
export function dirtyKeys(dirty: DirtyMap): (keyof AgentFormValues)[] {
  return (Object.keys(dirty) as (keyof AgentFormValues)[]).filter((k) => isDirty(dirty[k]))
}

export function hasTrackedChanges(dirty: DirtyMap): boolean {
  const keys = dirtyKeys(dirty)
  return TRACKED_FIELDS.some((f) => keys.includes(f))
}

/**
 * PATCH body with only the changed fields. Arrays (tags, skills, MCP
 * assignments) are sent whole because the server replaces them.
 */
export function agentPatch(values: AgentFormValues, dirty: DirtyMap, note?: string): AgentInput {
  const body: Record<string, unknown> = {}
  for (const k of dirtyKeys(dirty)) {
    const v = values[k]
    body[k] = typeof v === "string" && k === "name" ? v.trim() : v
  }
  if (note?.trim() && hasTrackedChanges(dirty)) body.revision_note = note.trim()
  return body as AgentInput
}

/**
 * Current (possibly unsaved) editor state as one-turn playground overrides.
 * MCP/skill assignments are only overridden when they were changed, because an
 * override replaces the saved assignments (and drops per-agent tool switches).
 */
export function agentOverrides(v: AgentFormValues, dirty: DirtyMap = {}): Overrides {
  const keys = dirtyKeys(dirty)
  const o: Overrides = {
    system_prompt: v.system_prompt,
    model_id: v.model_id,
    temperature: v.temperature,
    top_p: v.top_p,
    top_k: v.top_k,
    thinking: v.thinking_enabled,
    cache_static: v.prompt_cache_static,
    cache_messages: v.prompt_cache_messages,
  }
  if (keys.includes("skill_ids")) o.skill_ids = v.skill_ids
  if (keys.includes("mcp_servers")) o.mcp_server_ids = v.mcp_servers.map((m) => m.mcp_server_id)
  return o
}

/** Defaults for a brand-new agent. */
export function newAgentDefaults(modelId?: number): Pick<AgentInput, "temperature" | "max_steps"> & {
  model_id?: number
} {
  return { model_id: modelId, temperature: 0.7, max_steps: 50 }
}
